<template>
  <div
    ref="tooltip"
    class="tooltip-balloon"
    :class="`tooltip-balloon--${tooltip.position}`"
    @mouseleave="mouseLeaveHandler">
    <div class="tooltip-balloon__body">
      <vnodes :vnodes="tooltip.content" />
    </div>
  </div>
</template>

<script>
import { supportsPassiveEvent, throttle } from 'Root/helpers/tools';
import { mapState } from 'vuex';

const minTooltipMargin = 8;

export default {
  name: 'TooltipLayerBalloon',

  components: {
    Vnodes: {
      functional: true,
      render: (h, ctx) => ctx.props.vnodes,
    },
  },

  props: {
    tooltip: {
      type: Object,
      required: true,
    },

    layer: {
      // гранд хак для SSR
      type: typeof window !== 'undefined' ? HTMLElement : Object,
      required: true,
    },
  },

  computed: {
    ...mapState(['userDevice']),

    throttledPositionSetter() {
      return throttle(() => {
        if (!this.tooltip.isVisible) {
          return;
        }

        this.setTooltipPosition();
      }, 25);
    },
  },

  watch: {
    'tooltip.isVisible': {
      handler(newValue) {
        if (newValue) {
          this.setTooltipPosition();
        }
      },
    },
  },

  mounted() {
    this.setTooltipPosition();
    window.addEventListener('resize', this.throttledPositionSetter);
    window.addEventListener('scroll', this.throttledPositionSetter, supportsPassiveEvent ? { passive: true } : false);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.throttledPositionSetter);
    window.removeEventListener('scroll', this.throttledPositionSetter);
  },

  methods: {
    setTooltipPosition() {
      // nextTick позволяет расчитать layout и getBoundingClientRect не разносит все к чертям
      this.$nextTick(() => {
        const { tooltip } = this.$refs;

        // слой тултипов
        const layerBounding = this.layer.getBoundingClientRect();
        // сам тултип
        const tooltipBounding = tooltip.getBoundingClientRect();
        // контейнер из которого тултип был вызван
        const containerBounding = this.tooltip.container.getBoundingClientRect();

        const tooltipPosition = this.tooltip.position || 'top';

        const containerHalfWidth = containerBounding.width / 2;
        const tooltipHalfWidth = tooltipBounding.width / 2;

        // координаты горизонтального центра контейнера
        const containerHorizontalCenter = containerBounding.left + containerHalfWidth;

        // ставим тултип всегда по центру контейнера
        const positionX = containerHorizontalCenter - tooltipHalfWidth;
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        let positionY = 0;

        if (tooltipPosition === 'top') {
          positionY = scrollTop + containerBounding.top - tooltipBounding.height - minTooltipMargin;
        } else {
          positionY = scrollTop + containerBounding.bottom + minTooltipMargin;
        }

        // определяем расстояние выхода за края чтобы сдвинуть и тултип и треугольничек
        let shiftX = 0;

        const rightAvailableSpace = layerBounding.width - containerHorizontalCenter - minTooltipMargin;
        const leftAvailableSpace = containerHorizontalCenter - minTooltipMargin;

        if (tooltipHalfWidth > rightAvailableSpace) {
          // если вылетает за правую границу
          shiftX = -1 * (tooltipHalfWidth - rightAvailableSpace);
        } else if (tooltipHalfWidth > leftAvailableSpace) {
          // если вылетает за левую границу
          shiftX = tooltipHalfWidth - leftAvailableSpace;
        }

        // ставим тултип по центру контейнера
        tooltip.style.transform = `translate(${positionX}px, ${positionY}px)`;

        // применяем сдвиг если перевалились за край
        if (shiftX) {
          tooltip.style.transform += ` translateX(${shiftX}px)`;
          tooltip.style.setProperty('--shiftX', `${shiftX}px`); // для треугольничка
        } else {
          tooltip.style.setProperty('--shiftX', null); // для треугольничка
        }
      });
    },

    mouseLeaveHandler() {
      const { triggers } = this.tooltip;
      if (!triggers.includes('hover') || (triggers.includes('click') && this.userDevice.isCoarse)) {
        return;
      }

      this.$store.commit('tooltip/mutateSetTooltipVisibility', {
        token: this.tooltip.token,
        status: false,
      });
    },
  },
};
</script>

<style>
  .tooltip-balloon__body a {
    color: inherit;
    text-decoration: underline;
  }
</style>

<style lang="scss" scoped>
  .tooltip-balloon {
    --width: 240px;
    --tooltip-color: #{$color--basic-blue};
    --shiftX: 0px;
    --border-radius: 5px;
    --padding: 8px 12px;
    --tooltip-text-color: #{$color--basic-white};

    position: absolute;
    max-width: var(--width);
    background-color: var(--tooltip-color);
    border-radius: var(--border-radius);
    padding: var(--padding);
    color: var(--tooltip-text-color);

    &:before {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      width: 100%;
      height: 7px;
    }

    :after {
      content: '';
      display: block;
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
      left: 50%;
      transform: translateX(-50%) translateX(calc(-1 * var(--shiftX))); // hacks
    }
  }

  .tooltip-balloon--top {
    &:before {
      top: 100%;
    }

    :after {
      border-width: 7px 9px 0 9px;
      border-color: var(--tooltip-color) transparent transparent transparent;
      bottom: -6px;
    }
  }

  .tooltip-balloon--bottom {
    &:before {
      bottom: 100%;
    }

    :after {
      border-width: 0 9px 7px 9px;
      border-color: transparent transparent var(--tooltip-color) transparent;
      top: -6px;
    }
  }

  .tooltip-balloon--info {
    --tooltip-color: #{$color--basic-blue};
  }

  .tooltip-balloon--info-gray {
    --tooltip-color: #{$color--basic-dark-gray};

    border-radius: 10px;
  }

  .tooltip-balloon--error {
    --tooltip-color: #{$color--error};
  }

  .tooltip-balloon--warning-red {
    --tooltip-color: #{$color--warning-red};
  }

  .tooltip-balloon--warning-orange {
    --tooltip-color: #{$color--warning-orange};
  }

  .tooltip-balloon--warning-yellow {
    --tooltip-color: #{$color--warning-yellow};
  }

  .tooltip-balloon--executor-card {
    --width: auto;
    --tooltip-color: #{$color--basic-dark-gray};

    padding: 16px;
  }

  .tooltip-balloon--entry-user {
    --tooltip-color: #{$color--basic-dark-gray};
    --border-radius: 15px;
    --padding: 20px;
  }

  .tooltip-balloon--header-profile {
    --tooltip-color: #{$color--basic-menu};
    --padding: 16px;
    --border-radius: 10px;

    left: 0;
  }

  @media screen and #{$screen-more-or-1024} {
    .tooltip-balloon--header-profile {
      left: -12px;
    }
  }
</style>
